import { FormEvent, useEffect, useState } from 'react';
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import useSafeLogger from 'src/hooks/useSafeLogger';
import styles from './StripeElementsUpdatePaymentMethod.module.scss';
import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { useVisible, useColorTheme, useGetUser, useSdkLoader } from 'src/hooks';
import { useCreateSubscriptionMutation } from 'src/store/services';
import { env } from 'src/env';
import { toast } from 'react-toastify';
import { PAYMENT_ERROR, PAYMENT_ERROR_MESSAGE } from 'src/constants';
import { GTMEvent, SubscriptionPlanInfo } from 'src/types';
import { sendGTMEvent, sendPaymentGTMEvent } from 'src/utils';
import { Button } from 'src/v2/commonComponents/Button';
import { PaymentProgressBar } from 'src/pages/PaymentBetaPage/componentsV2/PaymentProgressBar';

const PAYMENT_PROVIDER = 'stripe';

interface StripeElementsUpdatePaymentMethodProps {
  selectedPlan?: SubscriptionPlanInfo;
  onPaymentProcessing: (value: boolean) => void;
  numberOfSeats?: number;
}

export const StripeElementsUpdatePaymentMethod = ({
  selectedPlan,
  onPaymentProcessing,
  numberOfSeats,
}: StripeElementsUpdatePaymentMethodProps) => {
  const { pgLogError } = useSafeLogger();
  const stripe = useStripe();
  const elements = useElements();
  const { isDarkTheme } = useColorTheme();

  const { user_id, email } = useGetUser();

  const [createSubscription] = useCreateSubscriptionMutation();

  const {
    timedOut: isStripeSdkTimedOut,
    loading: isStripeSdkLoading,
    start: startStripeSdk,
    resolve: resolveStripeSdk,
    reset: resetStripeSdk,
  } = useSdkLoader();

  useEffect(() => {
    startStripeSdk();

    return () => {
      resetStripeSdk();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isReadyExpressCheckout, setReadyExpressCheckout] =
    useState<boolean>(false);
  const [isReadyPaymentElement, setReadyPaymentElement] =
    useState<boolean>(false);

  useEffect(() => {
    if (isReadyExpressCheckout && isReadyPaymentElement) {
      resolveStripeSdk();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyExpressCheckout, isReadyPaymentElement]);

  useEffect(() => {
    if (isStripeSdkTimedOut) {
      pgLogError(PAYMENT_ERROR, {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'stripe sdk timed out',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStripeSdkTimedOut]);

  const {
    isVisible: isSubmitDisabled,
    onVisibilitySet: setSubmitDisabled,
    onVisibilityRemove: setSubmitEnabled,
  } = useVisible(true);

  const handleCreateSubscription = async () => {
    if (!selectedPlan) return;
    try {
      const result = await createSubscription({
        user_id,
        seat_count: numberOfSeats,
        plan_id: selectedPlan?.plan_id,
      }).unwrap();

      return result;
    } catch (error) {
      onPaymentProcessing(false);
      toast.error(PAYMENT_ERROR_MESSAGE);

      pgLogError(PAYMENT_ERROR, {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'create subscription',
        error: JSON.stringify(error),
      });

      return;
    }
  };

  const handleConfirmPayment = async () => {
    if (!elements || !stripe) {
      return;
    }

    const subscriptionIntent = await handleCreateSubscription();

    if (!subscriptionIntent) {
      return;
    }

    const { client_secret, subscription_id, flow } = subscriptionIntent;

    const returnUrl = `${
      env.REACT_APP_NINJA_UI_URL
    }?subscription_succeeded_plan=${
      selectedPlan?.plan_tier
    }&subscription_succeeded_period=${selectedPlan?.period}&subscription_succeeded_value=${Math.round(
      (selectedPlan?.original_price || 0) / 100,
    )}&subscription_succeeded_currency=${selectedPlan?.currency || 'USD'}${
      numberOfSeats
        ? '&subscription_succeeded_number_of_seats=' + numberOfSeats
        : ''
    }`;

    if (flow === 'setup_intent') {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email,
            },
          },
        },
        redirect: 'if_required',
      });
      if (setupIntent && setupIntent.status === 'succeeded') {
        // !!! (olha): it's an important event we are tracking. We need to make sure it calls properly and only once by the subscription
        sendPaymentGTMEvent({
          transaction_id: subscription_id,
          subscription_plan: selectedPlan?.plan_tier,
          subscription_period: selectedPlan?.period,
          number_of_seats: numberOfSeats ? numberOfSeats.toString() : undefined,
          value: Math.round((selectedPlan?.original_price || 0) / 100),
          currency: selectedPlan?.currency || 'USD',
        });
      } else {
        toast.error(PAYMENT_ERROR_MESSAGE);

        pgLogError(PAYMENT_ERROR, {
          user_id,
          payment_provider: PAYMENT_PROVIDER,
          plan: selectedPlan?.plan_id,
          step: 'confirm payment',
          error: JSON.stringify(error),
        });
      }
    } else {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // !!! (olha): it's an important event we are tracking. We need to make sure it calls properly and only once by the subscription
        sendPaymentGTMEvent({
          transaction_id: subscription_id,
          subscription_plan: selectedPlan?.plan_tier,
          subscription_period: selectedPlan?.period,
          number_of_seats: numberOfSeats ? numberOfSeats.toString() : undefined,
          value: Math.round((selectedPlan?.original_price || 0) / 100),
          currency: selectedPlan?.currency || 'USD',
        });
      } else {
        toast.error(PAYMENT_ERROR_MESSAGE);

        pgLogError(PAYMENT_ERROR, {
          user_id,
          payment_provider: PAYMENT_PROVIDER,
          plan: selectedPlan?.plan_id,
          step: 'confirm payment',
          error: JSON.stringify(error),
        });
      }
    }
  };

  const handleSubmit = async (
    event:
      | StripeExpressCheckoutElementConfirmEvent
      | FormEvent<HTMLFormElement>,
  ) => {
    if ('preventDefault' in event) {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      event.preventDefault();
    }

    if (!elements || !stripe) {
      return;
    }

    onPaymentProcessing(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onPaymentProcessing(false);

      pgLogError(PAYMENT_ERROR, {
        user_id,
        payment_provider: PAYMENT_PROVIDER,
        plan: selectedPlan?.plan_id,
        step: 'stripe form validation',
        error: JSON.stringify(submitError),
      });

      return;
    }

    await handleConfirmPayment();
  };

  const handlePaymentElementChange = ({
    complete,
    value,
    collapsed,
  }: StripePaymentElementChangeEvent) => {
    if (complete) {
      setSubmitEnabled();
    } else {
      setSubmitDisabled();
    }

    // !!! (olha): if we change defaultCollapsed to false, GTM event don't work properly
    if (!collapsed) {
      sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
        payment_method_type: value.type,
      });
    }
  };

  const handleExpressCheckoutClick = (
    event: StripeExpressCheckoutElementClickEvent,
  ) => {
    sendGTMEvent(GTMEvent.PAYMENT_METHOD_CLICK, {
      payment_method_type: event.expressPaymentType,
    });
    event.resolve();
  };

  const handleSubmitClick = () => {
    sendGTMEvent(GTMEvent.PAYMENT_SUBMIT_BUTTON_CLICK);
  };

  if (isStripeSdkTimedOut) {
    return (
      <div className={styles.errorContainer}>
        <div>
          We couldn't connect to Stripe at the moment. Please refresh the page
          or try a different payment method.
        </div>
      </div>
    );
  }

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit}
      data-e2e="stripe-elements-form"
    >
      {isStripeSdkLoading && <PaymentProgressBar />}

      <ExpressCheckoutElement
        options={{
          paymentMethods: {
            googlePay: 'always',
            link: 'never', // never so it's shown in payment element below
            paypal: 'never',
          },
          paymentMethodOrder: ['apple_pay', 'google_pay', 'amazon_pay'],
          buttonTheme: {
            googlePay: 'white',
            applePay: isDarkTheme ? 'white' : 'black',
          },
          layout: {
            maxColumns: 1,
            maxRows: 5,
          },
          buttonHeight: 53,
        }}
        onReady={() => setReadyExpressCheckout(true)}
        onConfirm={handleSubmit}
        onClick={handleExpressCheckoutClick}
      />
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            // !!! (olha): if we change defaultCollapsed to false, GTM event don't work properly
            defaultCollapsed: true,
            radios: false,
            spacedAccordionItems: true,
          },
          fields: {
            billingDetails: {
              email: 'never',
            },
          },
          paymentMethodOrder: ['google_pay', 'apple_pay', 'card', 'cashapp'],
        }}
        onReady={() => setReadyPaymentElement(true)}
        onChange={handlePaymentElementChange}
      />
      <Button
        disabled={isSubmitDisabled}
        type="submit"
        onClick={handleSubmitClick}
        data-e2e="subscribe-submit-button"
      >
        Submit
      </Button>
    </form>
  );
};
